/**
 * Mapping helpers between the friendly Health Scoring controls on the
 * Exception Type form and the raw `exception_types` columns. No backend
 * contract changes - these only translate UI choices to/from the existing
 * `weight`, `sample_type`, `sample_value` fields.
 *
 * Backend semantics (backend/shared/exception_display/health.py):
 *  - participation gate = `good_threshold IS NOT NULL`
 *  - weight is a 1-10 linear multiplier (missing → treated as 1)
 *  - thresholds are ascending `<=` bands: good ≤ watch ≤ poor, else Critical
 */

export type ImpactBucket = 'low' | 'standard' | 'high' | 'critical'

/** Representative weight written when the user picks a bucket. */
export const WEIGHT_BY_BUCKET: Record<ImpactBucket, number> = {
  low: 1,
  standard: 3,
  high: 6,
  critical: 10,
}

export const IMPACT_OPTIONS: ReadonlyArray<{ label: string; value: ImpactBucket }> = [
  { label: 'Low', value: 'low' },
  { label: 'Standard', value: 'standard' },
  { label: 'High', value: 'high' },
  { label: 'Critical', value: 'critical' },
]

/** One-line description of what each impact bucket means for the score. */
export const IMPACT_DESCRIPTIONS: Record<ImpactBucket, string> = {
  low: 'Minor - small effect on the FP health score.',
  standard: 'Default - moderate effect on the score.',
  high: 'Significant - weighs heavily on the score.',
  critical: 'Severe - dominates the score for this type.',
}

/**
 * Bucket a stored numeric weight for display. Bands are centred on the
 * representative write-back values in WEIGHT_BY_BUCKET (1/3/6/10) so each pick
 * round-trips to its own label, while any legacy weight still lands in the
 * nearest bucket; the stored number only changes if the user actually picks one.
 */
export function bucketFromWeight(weight: number | null | undefined): ImpactBucket {
  if (weight == null) return 'standard'
  if (weight <= 2) return 'low'
  if (weight <= 4) return 'standard'
  if (weight <= 8) return 'high'
  return 'critical'
}

export type SampleWindow = 'all_time' | 'last_30' | 'custom'

export const SAMPLE_WINDOW_OPTIONS: ReadonlyArray<{ label: string; value: SampleWindow }> = [
  { label: 'All time', value: 'all_time' },
  { label: 'Last 30 days', value: 'last_30' },
  { label: 'Custom', value: 'custom' },
]

/** Derive the window control from stored sample_type/sample_value. */
export function windowFromSample(
  sampleType: string,
  sampleValue: string,
): SampleWindow {
  if (sampleType === 'last_days') {
    return sampleValue === '30' ? 'last_30' : 'custom'
  }
  // Default (all_time or unset) shows "All time".
  return 'all_time'
}

/**
 * Map the window control back to the stored pair. Returns the sample_type and
 * sample_value strings; `customDays` supplies the value for the custom window.
 */
export function sampleFromWindow(
  window: SampleWindow,
  customDays: string,
): { sample_type: string; sample_value: string } {
  if (window === 'all_time') return { sample_type: 'all_time', sample_value: '' }
  if (window === 'last_30') return { sample_type: 'last_days', sample_value: '30' }
  return { sample_type: 'last_days', sample_value: customDays }
}

export interface ThresholdBand {
  key: 'good' | 'watch' | 'poor' | 'critical'
  label: string
  /** Human range, e.g. "≤0", "1-2", "3", ">3". */
  range: string
  /** Status emoji for the preview row. */
  icon: string
  /** MUI palette colour token for the dot/text. */
  color: 'success' | 'warning' | 'error'
}

/** Integer-friendly "lo+1..hi" range string (single value when the band is one step). */
function bandRange(lo: number, hi: number): string {
  return hi > lo + 1 ? `${lo + 1}-${hi}` : `${hi}`
}

/**
 * Structured preview of the four backend score bands (Good ≤good, Watch ≤watch,
 * Poor ≤poor, Critical >poor - the open top band defined by poor_threshold, no
 * separate column). Returns [] until good ≤ watch ≤ poor all parse, so the UI
 * can hide the preview while inputs are incomplete/non-ascending.
 */
export function thresholdBands(good: string, watch: string, poor: string): ThresholdBand[] {
  if ([good, watch, poor].some((v) => v === '' || isNaN(Number(v)))) return []
  const g = Number(good)
  const w = Number(watch)
  const p = Number(poor)
  if (!(g <= w && w <= p)) return []
  return [
    { key: 'good', label: 'Good', range: `≤${g}`, icon: '✅', color: 'success' },
    { key: 'watch', label: 'Watch', range: bandRange(g, w), icon: '⚠️', color: 'warning' },
    { key: 'poor', label: 'Poor', range: bandRange(w, p), icon: '🔶', color: 'error' },
    { key: 'critical', label: 'Critical', range: `>${p}`, icon: '⛔', color: 'error' },
  ]
}
